import type { ReactElement } from 'react'
import { Page } from 'core/components'
import { Seo } from 'components'
import { Accordion, Anchor, Button, Icon, Text } from 'ui/core'
import { siteMetadata } from 'core/utils'
import useLiveChat from 'hooks/use-live-chat'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { FAQPageJsonLd } from 'next-seo'

const FAQPageSeo = () => (
  <>
    <Seo title="FAQs" description="Frequently Asked Questions" />
    <FAQPageJsonLd
      mainEntity={[
        {
          questionName: `What is ${siteMetadata.name}?`,
          acceptedAnswerText: `We help you sell or swap your used or new phones, laptops, tablet, smartwatches, and more gadgets at amazing prices and pay you instantly, all from the comfort of your home, so you can focus on the things that matter most in your life.`,
        },
        {
          questionName: 'How does it work?',
          acceptedAnswerText:
            'When you are ready to sell something, you provide us with some description including photos of your product. Our team will review the information and give you an offer based on the current market value and condition of your product. If you accept the offer, you can either bring the product to us or request to have it picked up by our delivery team, then you get paid after we confirmed that the product matches the description you provided.',
        },
        {
          questionName: 'How can I contact you?',
          acceptedAnswerText: `You can also reach out to us by sending an email to ${siteMetadata.contact.email} or via WhatsApp on this number ${siteMetadata.contact.formattedPhoneNumber}`,
        },
        {
          questionName: 'What brands do you buy, sell or swap?',
          acceptedAnswerText: `We buy, sell or swap a variety of brands such as Apple, Samsung, HP, Lenovo, Dell, Asus,
          and many others.`,
        },
        {
          questionName: 'Are your products new or used?',
          acceptedAnswerText: `We sell brand new, UK used and Naija used products.`,
        },
      ]}
    />
  </>
)

const FAQSPage = () => {
  const router = useRouter()
  const liveChat = useLiveChat()

  const faqs = [
    {
      question: `What is ${siteMetadata.name}?`,
      answer: (
        <>
          We help you sell or swap your used or new{' '}
          <Anchor component={Link} href="/sell/smartphone">
            phones
          </Anchor>
          ,{' '}
          <Anchor component={Link} href="/sell/laptop">
            laptops
          </Anchor>
          ,{' '}
          <Anchor component={Link} href="/sell?wearable">
            smartwatches
          </Anchor>
          ,{' '}
          <Anchor component={Link} href="/sell/tablets">
            tablets
          </Anchor>
          ,{' '}
          <Anchor component={Link} href="/sell">
            and more gadgets
          </Anchor>{' '}
          at amazing prices and pay you instantly, all from the comfort of your home, so you can
          focus on the things that matter most in your life. You could be selling because you want
          an upgrade, or for quick cash. You don&apos;t have to go through the stress of talking to
          multiple potential buyers when you can just sell it instantly on{' '}
          <Anchor component={Link} href="/">
            {siteMetadata.name}
          </Anchor>
          .
        </>
      ),
    },
    {
      question: `How does it work?`,
      answer: (
        <>
          When you are ready to sell something, you provide us with some description including
          photos of your product. Our team will review the information and give you an offer based
          on the current market value and condition of your product. If you accept the offer, you
          can either bring the product to us or request to have it picked up by our delivery team,
          then you get paid after we confirmed that the product matches the description you
          provided.
        </>
      ),
    },
    {
      question: `How can I contact you?`,
      answer: (
        <>
          You can chat with us right here by{' '}
          <Button
            size="compact-md"
            variant="light"
            onClick={() => liveChat.open()}
            rightSection={<Icon name="message2" />}
          >
            pressing this button
          </Button>
          . You can also reach out to us by sending an email to{' '}
          <Anchor href={`mailto:${siteMetadata.contact.email}`}>
            {siteMetadata.contact.email}
          </Anchor>
          , or via WhatsApp on this number{' '}
          <Anchor target="_blank" href={`https://wa.me/${siteMetadata.contact.phoneNumber}`}>
            {siteMetadata.contact.formattedPhoneNumber}
          </Anchor>
          . We will be glad to attend to any issues and answer any questions you may have.
        </>
      ),
    },
    {
      question: `What brands do you buy, sell or swap?`,
      answer: (
        <>
          We buy, sell or swap a variety of brands such as Apple, Samsung, HP, Lenovo, Dell, Asus,
          and many others.
        </>
      ),
    },
    {
      question: `Are your products new or used?`,
      answer: <>We sell brand new, UK used and Naija used products.</>,
    },
  ]

  return (
    <Page
      title="Frequently Asked Questions"
      py="xl"
      my="xl"
      onClickBack={() => router.back()}
    >
      <FAQPageSeo />
      <Accordion
        multiple
        radius="md"
        variant="separated"
        defaultValue={[faqs[0].question]}
      >
        {faqs.map((faq) => (
          <Accordion.Item key={faq.question} value={faq.question}>
            <Accordion.Control>{faq.question}</Accordion.Control>
            <Accordion.Panel>
              <Text size="md">{faq.answer}</Text>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Page>
  )
}

FAQSPage.getLayout = (page: ReactElement) => page

export default FAQSPage
